import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { FadeUp } from "@/components/site/Reveal";

const FAQS = [
    {
        q: "How long does a typical 3D visualization project take?",
        a: "Most residential projects deliver first draft renders in 7–14 days and final assets in 3–4 weeks. Timeline depends on scope, revisions, and how much walkthrough / VR content is included.",
    },
    {
        q: "Do you work with clients outside Hyderabad?",
        a: "Yes. We serve clients across India and remotely worldwide. Site visits are done in Hyderabad and Telangana; everywhere else we collaborate over video, drawings, and shared 3D previews.",
    },
    {
        q: "What do I need to give you to start?",
        a: "A rough sketch or 2D plan is enough. If you have a plot survey, elevations, or a Pinterest / reference board — even better. We take it from there and refine in an initial consultation.",
    },
    {
        q: "Can you handle both exterior and interior?",
        a: "Absolutely. We treat exterior, interior, landscape and lighting as one continuous story so the finished build feels considered from façade to fixtures.",
    },
    {
        q: "Do you provide VR walkthroughs?",
        a: "Yes. We offer both 360° walkthrough animations and true-scale VR experiences (headset supplied for local reviews) so you can inhabit the design before construction.",
    },
    {
        q: "What does pricing look like?",
        a: "Pricing is scoped per project — starting points depend on square footage, level of realism, and animation length. Book a free consultation for a written quote within 24 hours.",
    },
    {
        q: "Will you support us during construction?",
        a: "We stay on-call after delivery. Contractors regularly reach out to us on-site for material clarifications and detail resolution so the build stays true to the visualization.",
    },
];

export default function FAQ() {
    const [open, setOpen] = useState(0);
    return (
        <section id="faq" data-testid="faq" className="relative py-24 md:py-32">
            <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-4">
                    <div className="flex items-center gap-3 mb-6">
                        <span className="divider-gold" />
                        <span className="uppercase tracking-[0.32em] text-[11px] text-gold font-medium">
                            FAQ
                        </span>
                    </div>
                    <FadeUp>
                        <h2 className="font-display text-4xl md:text-6xl leading-tight tracking-tight">
                            Questions,<br />answered.
                        </h2>
                        <p className="mt-6 text-foreground/70 max-w-md">
                            Anything else on your mind? Reach out — we prefer real conversations
                            over templated FAQs.
                        </p>
                    </FadeUp>
                </div>

                <div className="lg:col-span-8">
                    <div className="divide-y hairline border-y hairline">
                        {FAQS.map((f, i) => {
                            const isOpen = open === i;
                            return (
                                <div key={i} data-testid={`faq-${i}`} className="py-5 md:py-6">
                                    <button
                                        onClick={() => setOpen(isOpen ? -1 : i)}
                                        data-testid={`faq-toggle-${i}`}
                                        aria-expanded={isOpen}
                                        aria-controls={`faq-panel-${i}`}
                                        className="w-full flex items-center justify-between gap-6 text-left group"
                                    >
                                        <span className="flex items-baseline gap-4 md:gap-6">
                                            <span className="font-mono text-xs text-gold tracking-widest w-8 shrink-0">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                            <span className="font-display text-xl md:text-2xl leading-snug tracking-tight group-hover:text-gold transition-colors">
                                                {f.q}
                                            </span>
                                        </span>
                                        <motion.span
                                            animate={{ rotate: isOpen ? 45 : 0 }}
                                            transition={{ duration: 0.3 }}
                                            className="shrink-0 h-9 w-9 rounded-full border hairline grid place-items-center"
                                        >
                                            <Plus className="h-4 w-4" />
                                        </motion.span>
                                    </button>
                                    <AnimatePresence initial={false}>
                                        {isOpen && (
                                            <motion.div
                                                key="c"
                                                id={`faq-panel-${i}`}
                                                role="region"
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.45, ease: [0.19, 1, 0.22, 1] }}
                                                className="overflow-hidden"
                                            >
                                                <p className="pt-5 md:pl-16 text-base md:text-lg text-foreground/70 leading-relaxed max-w-3xl">
                                                    {f.a}
                                                </p>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}
